import { createTheme, Palette, Theme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { ReactNode, useMemo } from 'react';
import { useGlobalState } from '@shared/components/common/GlobalStateContext';
import { useActorData } from '@shared/components/common/useAppData';
import { WhitelabelConfig } from '@lib/model/whitelabel-config-model';
import { WhitelabelTheme } from '@lib/model/whitelabel-theme-model';

// Define Inter font family for use in theme
const interFontFamily = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

export const defaultLightPrimaryColor = '#4674B9';

export const defaultLightPalette = {
  primary: { main: '#4775ba' },
  secondary: { main: '#e6ec79' },
  error: { main: '#D21034' },
  background: {
    default: '#ffffffff',
    paper: grey[50],
  },
  text: {
    primary: grey[900],
    secondary: grey[700],
  },
  header: {
    main: '#4775ba',
    contrastText: grey[50],
  },
  headerText: {
    primary: grey[50],
    secondary: '#d6df23',
  },
  sidebar: {
    main: grey[50],
    contrastText: grey[900],
  },
  sidebarText: {
    primary: grey[900],
    secondary: blue[500],
  },
} as const;

export const defaultDarkPalette = {
  primary: { main: '#90ACD5' },
  secondary: { main: '#45d3c3' },
  error: { main: '#fd7a92' },
  background: {
    default: '#04070a',
    paper: '#11171f',
  },
  text: {
    primary: '#ECF1F8',
    secondary: '#90ACD5',
  },
  header: {
    main: '#152338',
    contrastText: grey[50],
  },
  headerText: {
    primary: '#ECF1F8',
    secondary: '#90ACD5',
  },
  sidebar: {
    main: '#0E1725',
    contrastText: grey[50],
  },
  sidebarText: {
    primary: '#ECF1F8',
    secondary: '#90acd5',
  },
} as const;

const getDesignTokens = (mode: PaletteMode, whitelabelConfig?: WhitelabelConfig): ThemeOptions => ({
  typography: {
    fontFamily: interFontFamily,
  },
  palette: {
    ...(mode === 'light'
      ? materializeWhitelabelThemePalette(defaultLightPalette, whitelabelConfig?.lightTheme)
      : materializeWhitelabelThemePalette(defaultDarkPalette, whitelabelConfig?.darkTheme)),
    mode,
  },
  components: {
    // Override AppBar to always use dark theme styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          // Use dark theme header colors regardless of the current theme mode
          backgroundColor: `${defaultDarkPalette.header.main} !important`,
          color: `${defaultDarkPalette.headerText.primary} !important`,
          '& .MuiToolbar-root': {
            backgroundColor: `${defaultDarkPalette.header.main} !important`,
            color: `${defaultDarkPalette.headerText.primary} !important`,
          },
          '& .MuiTypography-root': {
            color: `${defaultDarkPalette.headerText.primary} !important`,
          },
          '& .MuiIconButton-root': {
            color: `${defaultDarkPalette.headerText.primary} !important`,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
          '& .MuiButton-root': {
            color: `${defaultDarkPalette.headerText.primary} !important`,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
          // Apply dark styling to any Toolpad-specific elements
          '& [data-toolpad-color-scheme]': {
            backgroundColor: `${defaultDarkPalette.header.main} !important`,
            color: `${defaultDarkPalette.headerText.primary} !important`,
          },
        },
      },
    },
    // Override Toolbar to ensure header toolbar uses dark styling
    MuiToolbar: {
      styleOverrides: {
        root: {
          // Target specifically the main header toolbar
          '&[role="banner"], &.MuiAppBar-root &': {
            backgroundColor: `${defaultDarkPalette.header.main} !important`,
            color: `${defaultDarkPalette.headerText.primary} !important`,
            '& .MuiTypography-root': {
              color: `${defaultDarkPalette.headerText.primary} !important`,
            },
            '& .MuiIconButton-root': {
              color: `${defaultDarkPalette.headerText.primary} !important`,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
            '& .MuiButton-root': {
              color: `${defaultDarkPalette.headerText.primary} !important`,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
          },
        },
      },
    },
    // Add global styles for any header-related elements
    MuiCssBaseline: {
      styleOverrides: {
        // Target Toolpad's header specifically
        '[data-toolpad="DashboardLayout"] header': {
          backgroundColor: `${defaultDarkPalette.header.main} !important`,
          color: `${defaultDarkPalette.headerText.primary} !important`,
          '& *': {
            color: `${defaultDarkPalette.headerText.primary} !important`,
          },
        },
        // Target any element with header-like attributes
        '[role="banner"]': {
          backgroundColor: `${defaultDarkPalette.header.main} !important`,
          color: `${defaultDarkPalette.headerText.primary} !important`,
          '& *': {
            color: `${defaultDarkPalette.headerText.primary} !important`,
          },
        },
      },
    },
  },
});

export function useTheme(mode: PaletteMode): Theme {
  const actorData = useActorData();
  const account = actorData.data?.account;
  return useMemo(() => {
    const lightTheme = getDesignTokens('light', account?.whitelabelConfig);
    const darkTheme = getDesignTokens('dark', account?.whitelabelConfig);
    return createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-toolpad-color-scheme',
      },
      colorSchemes: {
        light: lightTheme,
        dark: darkTheme,
      },
    });
  }, [account]);
}

export function CustomThemeProvider({ children }: { children: ReactNode | ReactNode[] }) {
  const { theme } = useGlobalState();
  const muiTheme = useTheme(theme);
  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
}

// Materialize the whitelabel theme

export type CustomTheme = typeof defaultLightPalette | typeof defaultDarkPalette;

export const materializeWhitelabelThemePalette = (
  defaultTheme: CustomTheme,
  customTheme: WhitelabelTheme = {},
) => {
  return {
    primary: { main: customTheme.primary || defaultTheme.primary.main },
    secondary: { main: customTheme.secondary || defaultTheme.secondary.main },
    error: { main: customTheme.error || defaultTheme.error.main },
    background: {
      default: customTheme.backgroundDefault || defaultTheme.background?.default,
      paper: customTheme.backgroundPaper || defaultTheme.background?.paper,
    },
    text: {
      primary: customTheme.textPrimary || defaultTheme.text?.primary,
      secondary: customTheme.textSecondary || defaultTheme.text?.secondary,
    },
    header: {
      main: customTheme.headerBackground || defaultTheme.header.main,
      contrastText: customTheme.headerText || defaultTheme.headerText?.primary,
    },
    headerText: {
      primary: customTheme.headerText || defaultTheme.headerText?.primary,
      secondary: customTheme.textSecondary || defaultTheme.text?.secondary,
    },
    sidebar: {
      main: customTheme.sidebarBackground || defaultTheme.sidebar.main,
      contrastText: customTheme.sidebarText || defaultTheme.sidebarText?.primary,
    },
    sidebarText: {
      primary: customTheme.sidebarText || defaultTheme.sidebarText?.primary,
      secondary: customTheme.textSecondary || defaultTheme.text?.secondary,
    },
  } as Palette;
};
